import { DISABLED_APY, filterTruthy, lamportsToUiAmount } from "@bridgesplit/utils";
import { StrategyDuration, TokenListMetadata, UncertaintyType, WhirlpoolPositionExpanded } from "@bridgesplit/abf-sdk";

import { useTokenListMetadataByMints } from "../reducers";
import {
    StrategyInfo,
    StrategyCollateralExpanded,
    MarketInformationWithLtvInfoResponse,
    LendingVaultInfo,
    VaultLtvInfo,
    VaultLtvInfos,
    ParsedLoanEventExpanded,
    PastLoanCollateral
} from "../types";
import { serializeStrategyDuration } from "./strategies";
import { getPastLoanCollateralMap } from "../api";

// use a hook to allow a later replacement with an api call to a token list
type MintsInput = (string | undefined)[] | undefined;

export function useGenerateUiExpanded(mints: MintsInput) {
    const { getMetadata, isLoading } = useTokenListMetadataByMints(mints);

    return {
        createStrategyCollateralExpanded: (
            strategyInfo: StrategyInfo,
            strategyMarketInfo?: MarketInformationWithLtvInfoResponse
        ) => createStrategyCollateralExpanded(strategyInfo, strategyMarketInfo, getMetadata),

        createLendingVaultLtvInfo: (
            lendingVault: LendingVaultInfo,
            marketInfo: MarketInformationWithLtvInfoResponse | undefined,
            strategyDurations: StrategyDuration[] | undefined
        ) => createLendingVaultLtvInfo(lendingVault, marketInfo, strategyDurations, getMetadata),

        createPastCollateralExpanded: (
            pastCollateral: ParsedLoanEventExpanded[],
            positionToWhirlpool: Map<string, WhirlpoolPositionExpanded> | undefined,
            getUsdPrice: (mint: string, uncertaintyType: UncertaintyType) => number | undefined
        ) => createPastCollateralExpanded(pastCollateral, getMetadata, positionToWhirlpool, getUsdPrice),

        isLoading
    };
}

export function createStrategyCollateralExpanded(
    strategyInfo: StrategyInfo,
    strategyMarketInfo: MarketInformationWithLtvInfoResponse | undefined,
    getMetadata: (mint: string) => TokenListMetadata | undefined
): StrategyCollateralExpanded[] {
    if (!strategyInfo.terms.assetTerms || !strategyMarketInfo) return [];

    const strategyCollateralExpanded: StrategyCollateralExpanded[] = [];

    for (const [mint, assetTerms] of Object.entries(strategyInfo.terms.assetTerms)) {
        const metadata = getMetadata(mint);
        if (!metadata) continue;

        const assetInfo = strategyMarketInfo.ltvInfo.find((asset) => asset.assetIdentifier === mint);
        if (!assetInfo) continue;

        const durationAndApys: Record<string, number> = {};
        assetTerms.durationAndApys.forEach(([duration, apy]: [StrategyDuration, number]) => {
            durationAndApys[serializeStrategyDuration(duration)] = apy;
        });

        strategyCollateralExpanded.push({
            metadata,
            durationAndApys,
            ltvInfo: {
                ltv: assetInfo.ltv,
                liquidationThreshold: assetInfo.liquidationThreshold
            }
        });
    }

    return strategyCollateralExpanded;
}

function createLendingVaultLtvInfo(
    lendingVault: LendingVaultInfo,
    marketInfo: MarketInformationWithLtvInfoResponse | undefined,
    strategyDurations: StrategyDuration[] | undefined,
    getMetadata: (mint: string) => TokenListMetadata | undefined
): Record<string, VaultLtvInfos> | undefined {
    if (!marketInfo || !strategyDurations) return undefined;

    const ltvInfos: Record<string, VaultLtvInfos> = {};
    const strategyTerms = lendingVault.vaultStrategy.terms;

    for (const marketLtvInfo of marketInfo.ltvInfo) {
        const collateralMint = marketLtvInfo.assetIdentifier;
        const mintAssetTerms = strategyTerms?.assetTerms?.[collateralMint];

        // Build a map of duration string -> apy for quick lookup
        const apyMap: Record<string, number> = {};
        if (mintAssetTerms && Array.isArray(mintAssetTerms.durationAndApys)) {
            for (const [duration, apy] of mintAssetTerms.durationAndApys) {
                apyMap[serializeStrategyDuration(duration)] = apy;
            }
        }

        const durationToLtvInfo: Record<string, VaultLtvInfo> = {};

        for (const duration of strategyDurations) {
            const key = serializeStrategyDuration(duration);
            const apy = apyMap[key] ? apyMap[key].toString() : DISABLED_APY;

            durationToLtvInfo[key] = {
                apy,
                marketInfo: {
                    ltv: marketLtvInfo.ltv,
                    liquidationThreshold: marketLtvInfo.liquidationThreshold,
                    oracleAccount: marketLtvInfo.oracleAccount,
                    oracleType: marketLtvInfo.oracleType
                }
            };
        }

        ltvInfos[collateralMint] = {
            durationToLtvInfo,
            collateralMetadata: getMetadata(collateralMint) ?? FALLBACK_TOKEN_LIST_METADATA,
            maxCollateralAllocationPct: marketLtvInfo.maxCollateralAllocationPct
        };
    }

    return ltvInfos;
}

//Util that takes past collateral and returns a list of past collateral expanded
function createPastCollateralExpanded(
    pastCollateral: ParsedLoanEventExpanded[],
    getMetadata: (mint: string) => TokenListMetadata | undefined,
    positionToWhirlpool: Map<string, WhirlpoolPositionExpanded> | undefined,
    getUsdPrice: (mint: string, uncertaintyType: UncertaintyType) => number | undefined
): PastLoanCollateral[] {
    const pastLoanCollateralMap = getPastLoanCollateralMap(pastCollateral);
    const eventMints: string[] = pastLoanCollateralMap ? Array.from(pastLoanCollateralMap.keys()) : [];

    const pastLoanCollateralItems: PastLoanCollateral[] = eventMints
        .map((mint) => {
            const amount = pastLoanCollateralMap?.get(mint) ?? 0;
            const metadata = getMetadata(mint);

            if (!metadata) {
                return undefined;
            }

            const whirlpool = positionToWhirlpool?.get(metadata.mint);

            const usdPrice = getUsdPrice(mint, UncertaintyType.Skip);

            return {
                usdPrice: usdPrice ?? 0,
                amount: lamportsToUiAmount(amount, metadata.decimals),
                metadata: metadata,
                assetType: metadata.assetType,
                whirlpoolPosition: whirlpool || undefined
            };
        })
        .filter(filterTruthy);

    return pastLoanCollateralItems;
}

const FALLBACK_TOKEN_LIST_METADATA: TokenListMetadata = {
    name: "Unknown",
    symbol: "Unknown",
    decimals: 0,
    mint: "Unknown",
    isPrincipal: false,
    isCollateral: false,
    imageUrl: "https://via.placeholder.com/150",
    tags: [],
    assetType: 0,
    tokenProgram: "token",
    numLuts: 0,
    custodian: "Unknown"
};
